import React, { useCallback, useEffect, useState } from 'react';

/**
 * useListFetch — the ONE list-fetch state machine for the CRM suite (design 01-architecture §4.1,
 * corpus P38/X-1). Ten surfaces hand-copied a fetch effect whose catch left the empty array in
 * place, so a FAILED load rendered as "there is nothing here" (eight surfaces) or as loading-forever
 * (two). This hook makes X-1 structural instead of per-surface:
 *
 *   status: 'loading' — nothing to show yet. First paint must be a skeleton, NEVER the empty copy.
 *   status: 'ready'   — data is trustworthy; an empty list now really means empty-by-truth.
 *   status: 'error'   — the fetch failed with nothing to show; render an error + retry, never [].
 *
 * Revalidation rule: once data exists, a refetch (refreshKey bump / retry) keeps the current rows on
 * screen and stays 'ready' — re-fetching is not "loading", and blanking a list someone is reading is
 * the reorder-under-the-reader defect. A refetch FAILURE with rows on screen keeps the rows (the
 * pre-A2 behaviour; the revalidation-error surface treatment is deliberately out of A2's scope).
 *
 * `setData` is exposed for surfaces that mutate optimistically (LeadsGrid edits, PipelineGrid stage
 * moves) — they own their rows between fetches; the hook owns where the rows CAME from.
 */
export interface ListFetchState<T> {
  status: 'loading' | 'ready' | 'error';
  data: T | null;
  setData: React.Dispatch<React.SetStateAction<T | null>>;
  retry: () => void;
}

export function useListFetch<T>(url: string | null, opts: { refreshKey?: unknown; map?: (json: any) => T } = {}): ListFetchState<T> {
  const { refreshKey, map } = opts;
  const [data, setData] = useState<T | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (url == null) return; // not ready to fetch yet (e.g. waiting on a dependent value) — stays 'loading'
    let alive = true;
    setStatus((s) => (s === 'ready' ? 'ready' : 'loading')); // silent revalidate when rows are up
    fetch(url)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((json) => {
        if (!alive) return;
        setData(map ? map(json) : (json as T));
        setStatus('ready');
      })
      .catch(() => {
        if (!alive) return;
        // 🔴 Never map a failure onto 'ready' with empty data — that is exactly the defect this hook
        // exists to remove. With rows already on screen, keep them (silent revalidate failure).
        setStatus((s) => (s === 'ready' ? 'ready' : 'error'));
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, refreshKey, attempt]);

  const retry = useCallback(() => { setStatus('loading'); setAttempt((a) => a + 1); }, []);
  return { status, data, setData, retry };
}
